import React, { useState, useEffect, useRef } from "react";
import { 
  FiSend, 
  FiChevronUp, 
  FiFileText, 
  FiDownload,
  FiCopy,
  FiThumbsUp,
  FiThumbsDown,
  FiShare2,
  FiStar,
  FiMaximize2,
  FiMinimize2,
  FiMessageSquare,
  FiZap,
  FiHelpCircle,
  FiCheck,
  FiAlertCircle,
  FiBookOpen,
  FiSearch,
  FiChevronRight,
  FiClock
} from "react-icons/fi";
import { askAI } from "../services/api";
import { db } from "../services/firebase";
import { doc, updateDoc } from "firebase/firestore";
import dayjs from "dayjs";
import "dayjs/locale/fr";

// Typing indicator amélioré
const TypingIndicator = () => (
  <div className="flex items-start gap-4 animate-fadeIn">
    <div className="relative">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-500 flex items-center justify-center shadow-lg">
        <span className="text-white font-bold text-sm">AI</span>
      </div>
      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-medium text-gray-700">L'assistant rédige...</span>
        <div className="flex gap-1">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-pulse"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-2 bg-gradient-to-r from-teal-100 to-teal-200 rounded-full w-full animate-pulse"></div>
        <div className="h-2 bg-gradient-to-r from-teal-100 to-teal-200 rounded-full w-5/6 animate-pulse"></div>
        <div className="h-2 bg-gradient-to-r from-teal-100 to-teal-200 rounded-full w-4/6 animate-pulse"></div>
      </div>
    </div>
  </div>
);

// Message actions component
const MessageActions = ({ onCopy, onLike, onDislike, isAI, liked, disliked }) => (
  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300">
    {isAI && (
      <>
        <button
          onClick={onLike}
          className={`p-2 rounded-lg transition-all duration-200 transform hover:scale-110 ${
            liked ? "bg-green-100 text-green-600" : "hover:bg-green-100 text-gray-500 hover:text-green-600"
          }`}
          title="Utile"
        >
          <FiThumbsUp size={16} />
        </button>
        <button
          onClick={onDislike}
          className={`p-2 rounded-lg transition-all duration-200 transform hover:scale-110 ${
            disliked ? "bg-red-100 text-red-600" : "hover:bg-red-100 text-gray-500 hover:text-red-600"
          }`}
          title="Pas utile"
        >
          <FiThumbsDown size={16} />
        </button>
      </>
    )}
    <button
      onClick={onCopy}
      className="p-2 rounded-lg hover:bg-teal-100 text-gray-500 hover:text-teal-600 transition-all duration-200 transform hover:scale-110"
      title="Copier"
    >
      <FiCopy size={16} />
    </button>
    <button
      className="p-2 rounded-lg hover:bg-purple-100 text-gray-500 hover:text-purple-600 transition-all duration-200 transform hover:scale-110"
      title="Partager"
    >
      <FiShare2 size={16} />
    </button>
  </div>
);

const AskAI = ({ document }) => {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [fullScreen, setFullScreen] = useState(false);
  const [messageFeedback, setMessageFeedback] = useState({});
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");

  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Questions suggérées
  const suggestedQuestions = [
    {
      id: 1,
      text: "Quels sont les points clés principaux ?",
      icon: FiZap,
      category: "Analyse"
    },
    {
      id: 2,
      text: "Peux-tu expliquer la conclusion ?",
      icon: FiHelpCircle,
      category: "Explication"
    },
    {
      id: 3,
      text: "Quelles sont les recommandations ?",
      icon: FiCheck,
      category: "Action"
    },
    {
      id: 4,
      text: "Y a-t-il des risques identifiés ?",
      icon: FiAlertCircle,
      category: "Risques"
    },
    {
      id: 5,
      text: "Qui sont les parties prenantes ?",
      icon: FiMessageSquare,
      category: "Acteurs"
    },
    {
      id: 6,
      text: "Résume en 3 points essentiels",
      icon: FiBookOpen,
      category: "Résumé"
    }
  ];

  // Initialisation
  useEffect(() => {
    setMessages(document?.conversation || []);
    setShowSuggestions(document?.conversation?.length === 0);
  }, [document]);

  // Focus sur l'input
  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [loading]);

  // Scroll management
  const scrollToBottom = (behavior = "smooth") => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  };

  useEffect(() => {
    if (isAtBottom && messages.length > 0) {
      scrollToBottom("auto");
    }
  }, [messages, loading, isAtBottom]);

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      const atBottom = scrollHeight - scrollTop - clientHeight < 50;
      setIsAtBottom(atBottom);
    }
  };

  // Copier le texte
  const handleCopyText = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Erreur lors de la copie:", err);
    }
  };

  // Gestion du feedback
  const handleFeedback = (messageId, type) => {
    setMessageFeedback(prev => ({
      ...prev,
      [messageId]: type
    }));
  };

  // Envoyer un message
  const handleSend = async () => {
    if (!question.trim() || !document?.id || loading) return;

    const userMessage = {
      id: Date.now(),
      type: "user",
      text: question.trim(),
      time: dayjs().format("HH:mm"),
      date: dayjs().format("DD MMM"),
      timestamp: Date.now()
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setQuestion("");
    setLoading(true);
    setIsAtBottom(true);
    setShowSuggestions(false);

    try {
      const res = await askAI(question, document?.summary || "");
      const aiMessage = {
        id: Date.now() + 1,
        type: "ai",
        text: res.answer || "Je n'ai pas pu générer de réponse.",
        time: dayjs().format("HH:mm"),
        date: dayjs().format("DD MMM"),
        timestamp: Date.now()
      };

      const updatedMessages = [...newMessages, aiMessage];
      setMessages(updatedMessages);

      // Sauvegarde
      const docRef = doc(db, "documents", document.id);
      await updateDoc(docRef, { conversation: updatedMessages });
    } catch (err) {
      console.error("Erreur:", err);
      const errorMessage = {
        id: Date.now() + 1,
        type: "ai",
        text: "Désolé, une erreur est survenue. Veuillez réessayer.",
        time: dayjs().format("HH:mm"),
        date: dayjs().format("DD MMM"),
        timestamp: Date.now()
      };
      const updatedMessages = [...newMessages, errorMessage];
      setMessages(updatedMessages);

      const docRef = doc(db, "documents", document.id);
      await updateDoc(docRef, { conversation: updatedMessages });
    }

    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Télécharger le résumé en PDF - VERSION CORRIGÉE
  const downloadSummaryAsPDF = async () => {
    if (!document?.summary || downloading) return;
    
    setDownloading(true);
    try {
      const { jsPDF } = await import("jspdf");
      
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      
      // Configurations
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      
      // Couleurs
      const primaryColor = [13, 148, 136]; // teal-600
      const secondaryColor = [102, 102, 102]; // gray-600
      
      // Titre
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(24);
      pdf.setTextColor(...primaryColor);
      pdf.text("Résumé du Document", margin, margin);
      
      // Informations du document
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(12);
      pdf.setTextColor(...secondaryColor);
      pdf.text(`Document: ${document.fileName}`, margin, margin + 10);
      pdf.text(`Généré le: ${dayjs().format("DD MMMM YYYY à HH:mm")}`, margin, margin + 16);
      
      // Ligne de séparation
      pdf.setDrawColor(...primaryColor);
      pdf.setLineWidth(0.5);
      pdf.line(margin, margin + 22, pageWidth - margin, margin + 22);
      
      // Résumé
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      
      // Préparation du texte
      const summaryText = document.summary;
      const splitText = pdf.splitTextToSize(summaryText, maxWidth);
      
      let yPosition = margin + 30;
      
      // Ajouter un fond léger
      pdf.setFillColor(240, 253, 250); // teal-50
      const textHeight = splitText.length * 6;
      const textBlockHeight = textHeight + 10;
      pdf.roundedRect(margin, yPosition - 5, maxWidth, textBlockHeight, 3, 3, 'F');
      
      // Bordure
      pdf.setDrawColor(221, 221, 221);
      pdf.roundedRect(margin, yPosition - 5, maxWidth, textBlockHeight, 3, 3, 'D');
      
      // Texte du résumé
      pdf.text(splitText, margin + 5, yPosition);
      
      yPosition += textBlockHeight + 15;
    
      // Pied de page
      const finalYPosition = yPosition + 10;
      pdf.setDrawColor(221, 221, 221);
      pdf.setLineWidth(0.5);
      pdf.line(margin, finalYPosition, pageWidth - margin, finalYPosition);
      
      pdf.setFontSize(9);
      pdf.setTextColor(102, 102, 102);
      pdf.text("Généré avec sumdocx - Votre assistant IA pour documents", margin, finalYPosition + 8);
      pdf.text(`© ${new Date().getFullYear()} sumdocx - Tous droits réservés`, margin, finalYPosition + 13);
      
      // Filigrane discret
      pdf.setFontSize(40);
      pdf.setTextColor(240, 240, 240);
      pdf.setGState(new pdf.GState({ opacity: 0.1 }));
      pdf.text("SUMDOCX", pageWidth / 2, pageWidth / 2, { angle: 45, align: "center" });
      
      // Sauvegarder le PDF
      const fileName = `Resume_${document.fileName.replace(/\.[^/.]+$/, "").replace(/[^a-z0-9]/gi, "_")}_${dayjs().format("YYYY-MM-DD")}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error("Erreur lors de la génération du PDF:", error);
      
      // Fallback: télécharger en TXT si PDF échoue
      try {
        const content = `
        RÉSUMÉ DU DOCUMENT
        ===================
        
        Document: ${document.fileName}
        Généré le: ${dayjs().format("DD MMMM YYYY à HH:mm")}
        
        ${document.summary}
        
        ${messages.length > 0 ? `
        DERNIÈRES CONVERSATIONS
        =======================
        
        ${messages.slice(-3).map(msg => 
          `${msg.type === 'user' ? '👤 Vous' : '🤖 Assistant'} (${msg.date} ${msg.time}):
          ${msg.text}
          
          `
        ).join('\n')}
        ` : ''}
        
        ---
        Généré avec sumdocx
        ${window.location.href}
        `;
        
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `resume_${document.fileName.replace(/\.[^/.]+$/, "").replace(/[^a-z0-9]/gi, "_")}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
      } catch (fallbackError) {
        console.error("Erreur lors du fallback TXT:", fallbackError);
        alert("Erreur lors du téléchargement. Veuillez réessayer.");
      }
    } finally {
      setDownloading(false);
    }
  };

  // Format date pour l'affichage
  const formatMessageDate = (timestamp) => {
    const date = dayjs(timestamp);
    const now = dayjs();
    
    if (date.isSame(now, 'day')) {
      return "Aujourd'hui";
    } else if (date.isSame(now.subtract(1, 'day'), 'day')) {
      return "Hier";
    } else if (date.isAfter(now.subtract(7, 'day'))) {
      return date.format("dddd");
    } else {
      return date.format("DD MMM");
    }
  };

  return (
    <div className={`flex flex-col h-full bg-gradient-to-br from-gray-50 to-white ${fullScreen ? "fixed inset-0 z-50" : ""}`}>
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 md:px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-end gap-4">
            {/* Actions */}
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab("summary")}
                  className={`px-4 py-2 rounded-md transition-all duration-200 ${
                    activeTab === "summary" 
                      ? "bg-white text-teal-600 shadow-sm" 
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Résumé
                </button>
                <button
                  onClick={() => setActiveTab("chat")}
                  className={`px-4 py-2 rounded-md transition-all duration-200 ${
                    activeTab === "chat" 
                      ? "bg-white text-teal-600 shadow-sm" 
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Chat
                </button>
              </div>
              
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={() => setFullScreen(!fullScreen)}
                  className="p-2.5 rounded-xl hover:bg-gray-100 text-gray-700 transition-all duration-200 hover:scale-105 active:scale-95"
                  title={fullScreen ? "Réduire" : "Plein écran"}
                >
                  {fullScreen ? <FiMinimize2 size={18} /> : <FiMaximize2 size={18} />}
                </button>
                <button
                  onClick={downloadSummaryAsPDF}
                  disabled={downloading || !document?.summary}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:from-teal-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow hover:shadow-md active:scale-95 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  {downloading ? (
                    <>
                      <div className="relative z-10 w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span className="relative z-10 hidden md:inline font-medium">Génération PDF...</span>
                    </>
                  ) : (
                    <>
                      <FiDownload size={18} className="relative z-10" />
                      <span className="relative z-10 hidden md:inline font-medium">Télécharger PDF</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Onglets mobiles */}
          <div className="flex md:hidden mt-4 border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 w-full bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab("summary")}
                className={`flex-1 px-4 py-2 rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${
                  activeTab === "summary" 
                    ? "bg-white text-teal-600 shadow-sm" 
                    : "text-gray-600"
                }`}
              >
                <FiFileText size={16} />
                <span>Résumé</span>
              </button>
              <button
                onClick={() => setActiveTab("chat")}
                className={`flex-1 px-4 py-2 rounded-md transition-all duration-200 flex items-center justify-center gap-2 ${
                  activeTab === "chat" 
                    ? "bg-white text-teal-600 shadow-sm" 
                    : "text-gray-600"
                }`}
              >
                <FiMessageSquare size={16} />
                <span>Chat</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <div className="flex-1 flex overflow-hidden">
        {/* Résumé */}
        {activeTab === "summary" && document?.summary && (
          <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-teal-50/50 to-white">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                          <FiBookOpen className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900">Résumé intelligent</h2>
                          <p className="text-gray-600">Généré automatiquement par l'IA</p>
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                          Analyse complète
                        </span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          Points clés
                        </span>
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                          Insights
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <button
                        onClick={() => setActiveTab("chat")}
                        className="px-5 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-colors flex items-center gap-2"
                      >
                        <FiMessageSquare size={18} />
                        <span>Poser une question</span>
                      </button>
                    </div>
                  </div>
                  
                  <div className="prose prose-lg max-w-none">
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {document.summary}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Questions fréquentes sur ce document</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {suggestedQuestions.slice(0, 4).map((q) => (
                        <button
                          key={q.id}
                          onClick={() => {
                            setQuestion(q.text);
                            setActiveTab("chat");
                            setTimeout(() => inputRef.current?.focus(), 100);
                          }}
                          className="text-left p-4 bg-white border border-gray-300 rounded-xl hover:border-teal-400 hover:shadow-sm transition-all duration-300"
                        >
                          <div className="flex items-center gap-3">
                            <q.icon className="w-5 h-5 text-teal-500" />
                            <span className="font-medium text-gray-800">{q.text}</span>
                          </div>
                          <span className="text-xs text-gray-500 mt-2 block">{q.category}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Chat */}
        {activeTab === "chat" && (
          <div className="flex-1 flex flex-col">
            {/* Zone de chat */}
            <div
              ref={chatContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-b from-white to-gray-50/50 scroll-smooth"
            >
              <div className="max-w-4xl mx-auto space-y-8">
                {/* Message d'introduction */}
                {messages.length === 0 && !loading && showSuggestions && (
                  <div className="animate-fadeIn">
                    <div className="text-center py-8 md:py-12">
                      <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 bg-gradient-to-br from-teal-100 to-teal-200 rounded-full flex items-center justify-center shadow-xl">
                        <FiMessageSquare className="w-10 h-10 md:w-12 md:h-12 text-teal-600" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                        Commencez la conversation
                      </h2>
                      <p className="text-gray-600 max-w-md mx-auto mb-8 text-lg">
                        Posez vos questions sur le document pour obtenir des réponses précises
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl mx-auto mb-8">
                        {suggestedQuestions.map((q) => (
                          <button
                            key={q.id}
                            onClick={() => {
                              setQuestion(q.text);
                              inputRef.current?.focus();
                            }}
                            className="text-left p-4 bg-white border border-gray-300 rounded-xl hover:border-teal-400 hover:shadow-sm transition-all duration-300"
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <q.icon className="w-5 h-5 text-teal-500" />
                              <span className="font-medium text-gray-800">{q.text}</span>
                            </div>
                            <span className="text-xs text-gray-500">{q.category}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Messages */}
                <div className="space-y-6">
                  {messages.map((msg, index) => {
                    const isFirstOfDay = index === 0 || formatMessageDate(messages[index - 1].timestamp) !== formatMessageDate(msg.timestamp);
                    
                    return (
                      <React.Fragment key={msg.id}>
                        {isFirstOfDay && (
                          <div className="relative flex items-center justify-center my-8">
                            <div className="absolute inset-0 flex items-center">
                              <div className="w-full border-t border-gray-300"></div>
                            </div>
                            <div className="relative px-4 bg-white">
                              <span className="text-sm font-medium text-gray-500 bg-white px-3 py-1 rounded-full border border-gray-300">
                                {formatMessageDate(msg.timestamp)}
                              </span>
                            </div>
                          </div>
                        )}

                        <div
                          className={`group animate-slideIn ${
                            msg.type === "user" 
                              ? "ml-auto max-w-3xl" 
                              : "mr-auto max-w-3xl"
                          }`}
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <div
                            className={`relative rounded-2xl transition-all duration-300  ${
                              msg.type === "user"
                                ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white"
                                : "bg-white border border-gray-200"
                            }`}
                          >
                            <div className="p-5">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <div className={`flex items-center justify-center ${
                                    msg.type === "user" 
                                      ? "w-8 h-8 rounded-full bg-white/20" 
                                      : "relative"
                                  }`}>
                                    {msg.type === "user" ? (
                                      <span className="text-sm font-bold"></span>
                                    ) : (
                                      <>
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-500 flex items-center justify-center">
                                          <span className="text-white font-bold text-xs">AI</span>
                                        </div>
                                        <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
                                      </>
                                    )}
                                  </div>
                                  <div>
                                    <span className={`text-sm font-medium ${
                                      msg.type === "user" ? "text-teal-100" : "text-gray-700"
                                    }`}>
                                      {msg.type === "user" ? "Vous" : "Assistant IA"}
                                    </span>
                                    <div className={`text-xs ${
                                      msg.type === "user" ? "text-teal-200" : "text-gray-500"
                                    }`}>
                                      {msg.time}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className={`text-xs ${
                                  msg.type === "user" ? "text-teal-200" : "text-gray-500"
                                }`}>
                                  {msg.time}
                                </div>
                              </div>

                              <div className={`mb-4 ${
                                msg.type === "user" ? "text-white" : "text-gray-800"
                              }`}>
                                <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                              </div>

                              <div className="flex items-center justify-between border-t border-gray-300/30">
                                <MessageActions
                                  onCopy={() => handleCopyText(msg.text, msg.id)}
                                  onLike={() => handleFeedback(msg.id, 'like')}
                                  onDislike={() => handleFeedback(msg.id, 'dislike')}
                                  isAI={msg.type === "ai"}
                                  liked={messageFeedback[msg.id] === 'like'}
                                  disliked={messageFeedback[msg.id] === 'dislike'}
                                />
                                
                                {copiedId === msg.id && (
                                  <span className="text-xs text-teal-500 font-medium animate-fadeIn flex items-center gap-1">
                                    <FiCheck />
                                    Copié !
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}

                  {loading && (
                    <div className="max-w-3xl">
                      <TypingIndicator />
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>
            </div>

            {/* Bouton scroll to bottom */}
            {!isAtBottom && (
              <button
                onClick={() => {
                  scrollToBottom();
                  setIsAtBottom(true);
                }}
                className="fixed bottom-24 right-6 md:right-8 p-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95 z-10"
              >
                <FiChevronUp className="w-5 h-5 rotate-180" />
              </button>
            )}

            {/* Zone d'entrée */}
            <div className="sticky bottom-0 border-t border-gray-200 bg-white/95 backdrop-blur-sm z-10">
              <div className="max-w-4xl mx-auto px-4 py-4">
                {showSuggestions && messages.length > 0 && (
                  <div className="mb-4 flex gap-2 overflow-x-auto pb-2">
                    {suggestedQuestions.slice(0, 4).map((q) => (
                      <button
                        key={q.id}
                        onClick={() => {
                          setQuestion(q.text);
                          inputRef.current?.focus();
                        }}
                        className="flex-shrink-0 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors whitespace-nowrap"
                      >
                        {q.text}
                      </button>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-3 items-end">
                  <div className="flex-1 relative">
                    <div className="relative">
                      <input
                        ref={inputRef}
                        type="text"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Posez votre question ici... (Appuyez sur Entrée pour envoyer)"
                        className="w-full p-4 pr-12 bg-gray-50 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-500/20 transition-all duration-300 text-gray-800 placeholder-gray-500 hover:border-gray-400"
                        disabled={loading}
                      />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                        ⏎
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleSend}
                    disabled={loading || !question.trim()}
                    className="relative p-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:from-teal-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 min-w-[60px] h-16 flex items-center justify-center group overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    {loading ? (
                      <div className="relative z-10 w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FiSend className="relative z-10 w-6 h-6" />
                    )}
                  </button>
                </div>
                
                <div className="mt-3 flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <FiAlertCircle className="w-3 h-3" />
                    L'IA peut faire des erreurs. Vérifiez les informations importantes.
                  </p>
                  <div className="flex items-center gap-4">
                    <p className="text-xs text-gray-500">
                      {question.length}/1000 caractères
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out forwards;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AskAI;